import { useQuery } from '@tanstack/react-query'
import { getOepDocumentDetails } from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import { APPROVAL_QUERY_KEYS } from 'modules/annual-benefit-planning/modules/approval/infra/react-query/approval.query-keys'
import type { ParsedContributionRow } from '../types'

interface Options {
  readonly clientId: string
  readonly documentId: string | null
}

interface Result {
  readonly rows: readonly ParsedContributionRow[]
  readonly isLoading: boolean
  readonly isError: boolean
}

/**
 * Backs the Analysis File detail popup (EMS-10154). Calls the real
 * GET contribution-model/by-document/:oepDocumentId endpoint
 * (abp-4-contribution-modeling module) — no longer the :documentId/details
 * mock. A 404 (no contribution model generated yet for this document)
 * surfaces the same way as any other fetch failure, via isError.
 */
export function useOepDocumentRows({ clientId, documentId }: Options): Result {
  const safeDocumentId = documentId ?? ''
  const enabled = clientId !== '' && documentId !== null

  const { data, isLoading, isError } = useQuery({
    queryKey: APPROVAL_QUERY_KEYS.oepDocumentDetails(clientId, safeDocumentId),
    queryFn: () => getOepDocumentDetails(safeDocumentId),
    enabled,
    // A 404/500 for one document isn't worth silently retrying for several
    // seconds before the popup's empty state settles.
    retry: false,
  })

  return { rows: data ?? [], isLoading: enabled && isLoading, isError }
}
